<div
  v-if="view_membership_loading"
  class="customer-profile-loader-wrap marketplace-loader-wrap d-flex align-items-center justify-content-center"
  role="status"
  aria-live="polite"
>
  <div class="marketplace-loader-card">
    <div class="marketplace-loader-orbit">
      <span class="marketplace-loader-ring marketplace-loader-ring-primary"></span>
      <span class="marketplace-loader-ring marketplace-loader-ring-secondary"></span>
      <span class="marketplace-loader-core d-flex align-items-center justify-content-center">
        <i class="bi bi-patch-check-fill"></i>
      </span>
      <span class="marketplace-loader-dot marketplace-loader-dot-one"></span>
      <span class="marketplace-loader-dot marketplace-loader-dot-two"></span>
    </div>
    <p class="marketplace-loader-title mb-1">Loading membership</p>
    <p class="marketplace-loader-copy mb-0">We are preparing your membership options.</p>
    <span class="visually-hidden">Loading membership...</span>
  </div>
</div>

<div v-else class="verification-flow">

    <HomeNavBar/>
    <MembershipCard ref="membership_card" modal-id="viewMembershipCardModal" />


    <div class="container-fluid">
      <div class="d-none mb-4 step-indicator">
        <div class="row justify-content-center">
          <div class="col-auto" v-for="(step, index) in steps" :key="index">
            <div class="step d-flex flex-column align-items-center" :class="{ active: currentStepIndex === index, completed: currentStepIndex > index }">
              <div class="step-circle" :class="currentStepIndex === index ? 'bg-secondary text-white border-primary' : currentStepIndex > index ? 'bg-primary text-white border-success' : 'bg-light text-secondary border border-2'">{{ index + 1 }}</div>
              <div class="step-label" :class="currentStepIndex === index ? 'text-secondary fw-bold' : currentStepIndex > index ? 'text-primary' : 'text-primary'">{{ step.label }}</div>
            </div>
          </div>
        </div>
      </div>


      <div v-if="currentStepIndex === 0" class="pb-extra">
        <button class="btn rounded-circle p-2 d-flex align-items-center justify-content-center membership-view-back-btn"
          @click="go_back"><i class="bi bi-arrow-left fs-4"></i>
        </button>
        <div class="membership-pricing-header text-center">
          <h2 class="text-primary fw-bold mb-2">
            {{ current_membership ? 'Update Your Membership Plan' : 'Choose Your Membership Plan' }}
          </h2>
          <p class="text-muted text-xxs mb-0">
            Select the plan that fits your access needs. You can upgrade or switch your membership at any time.
          </p>
        </div>

        <section
          v-if="billing_summary.subscriptions.length || billing_summary.invoices.length"
          class="card border-0 shadow-sm rounded-4 mb-4"
          aria-labelledby="membershipBillingHeading"
        >
          <div class="card-body p-4">
            <div class="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
              <div>
                <h3 id="membershipBillingHeading" class="h5 text-primary fw-bold mb-1">My membership billing</h3>
                <p class="text-muted text-xs mb-0">Review invoices and pay any outstanding membership fees securely.</p>
              </div>
              <div class="text-end">
                <div class="text-muted text-xxs text-uppercase">Outstanding</div>
                <div class="h4 fw-bold mb-0" :class="billing_summary.has_outstanding ? 'text-danger' : 'text-success'">
                  {{ format_currency(billing_summary.outstanding_total) }}
                </div>
              </div>
            </div>

            <div
              v-for="subscription in billing_summary.subscriptions"
              :key="subscription.membership_id"
              class="d-flex align-items-center justify-content-between gap-3 border rounded-3 p-3 mb-3"
            >
              <div>
                <div class="fw-bold">{{ subscription.membership_name }}</div>
                <div class="text-muted text-xs">
                  <span v-if="subscription.current_period_end">Current period ends {{ format_display_date(subscription.current_period_end) }}</span>
                  <span v-if="subscription.grace_until"> · Grace ends {{ format_display_date(subscription.grace_until) }}</span>
                </div>
              </div>
              <span class="badge text-capitalize" :class="billing_status_class(subscription.status)">
                {{ billing_status_label(subscription.status) }}
              </span>
            </div>

            <div class="table-responsive" v-if="billing_summary.invoices.length">
              <table class="table align-middle mb-0">
                <thead>
                  <tr class="text-muted text-xxs text-uppercase">
                    <th>Invoice</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th class="text-end">Amount</th>
                    <th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in billing_summary.invoices" :key="invoice.reference">
                    <td>
                      <div class="fw-semibold">{{ invoice.membership_name }}</div>
                      <div class="text-muted text-xxs">{{ invoice.reference }}</div>
                      <div v-if="invoice.payment_attempts?.[0]?.status === 'failed'" class="text-danger text-xxs mt-1">
                        Last payment failed. You can safely try again.
                      </div>
                    </td>
                    <td class="text-xs">{{ format_display_date(invoice.due_at) }}</td>
                    <td>
                      <span class="badge text-capitalize" :class="billing_status_class(invoice.status)">
                        {{ billing_status_label(invoice.status) }}
                      </span>
                    </td>
                    <td class="text-end fw-semibold">{{ format_currency(invoice.outstanding_amount || invoice.amount_due) }}</td>
                    <td class="text-end">
                      <button
                        v-if="invoice.payable"
                        type="button"
                        class="btn btn-primary btn-sm"
                        :disabled="Boolean(invoice_payment_reference)"
                        @click="pay_membership_invoice(invoice)"
                      >
                        <span v-if="invoice_payment_reference === invoice.reference" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                        Pay now
                      </button>
                      <span v-else-if="invoice.status === 'paid'" class="text-success text-xs fw-semibold">Paid</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <div
          v-if="active_membership_plans.length"
          class="membership-pricing-grid mb-3"
          :class="{
            'membership-pricing-grid-single': active_membership_plans.length === 1,
            'membership-pricing-grid-pair': active_membership_plans.length === 2
          }"
        >
          <div
            class="membership-pricing-card-wrap"
            v-for="(plan, index) in active_membership_plans"
            :key="plan.id"
          >
            <article
              class="membership-pricing-card"
              :class="{
                'membership-pricing-card-featured': is_current_plan(plan) || selectedPlan === index,
                'membership-pricing-card-muted': !is_current_plan(plan) && selectedPlan !== index
              }"
              @click="!is_current_plan(plan) && selectPlan(index)"
            >
              <div
                v-if="is_current_plan(plan) || selectedPlan === index || is_popular_plan(plan)"
                class="membership-pricing-ribbon"
              >
                {{
                  is_current_plan(plan)
                    ? 'Current Plan'
                    : selectedPlan === index
                      ? 'Selected Plan'
                      : 'Most Popular'
                }}
              </div>
              <div class="membership-pricing-card-body">
                <h3 class="membership-pricing-title text-capitalize">
                  {{ plan?.membership_type?.name }}
                </h3>
                <p
                  class="membership-pricing-description"
                  :class="{ 'membership-pricing-description-empty': !plan.description }"
                >
                  {{ plan?.description }}
                </p>

                <div class="membership-pricing-price">
                  <div v-if="(plan?.membership_pricing || []).length > 0">
                    <span class="membership-pricing-amount">
                      R {{ Number(plan?.membership_pricing[0].price).toLocaleString('en-ZA', { maximumFractionDigits: 0 }) }}
                    </span>
                    <span class="membership-pricing-period">/{{ plan?.billing_cycle?.name || '-' }}</span>
                  </div>
                  <span v-else class="membership-pricing-amount">N/A</span>
                </div>
                <div
                  v-if="plan?.billing_cycle?.duration_days && (plan?.membership_pricing || []).length"
                  class="text-muted text-xs mt-1"
                >
                  {{ format_currency(Number(plan.membership_pricing[0].price) / Number(plan.billing_cycle.duration_days)) }}
                  per day
                </div>

                <button
                  v-if="is_current_plan(plan)"
                  class="btn membership-pricing-action"
                  disabled
                >
                  Current
                </button>
                <button
                  v-else
                  class="btn membership-pricing-action"
                  @click.stop="selectPlan(index)"
                >
                  {{
                    userHasPaid
                      ? `Switch to ${plan?.membership_type?.name}`
                      : 'Subscribe'
                  }}
                </button>

                <h4 class="membership-pricing-features-title">Features:</h4>
                <ul class="list-unstyled membership-pricing-features">
                  <li
                    v-for="(feature, i) in get_visible_perks(plan)"
                    :key="i"
                    class="membership-pricing-feature"
                  >
                    <i class="bi bi-check-circle-fill" aria-hidden="true"></i>
                    <span>{{ feature?.perk?.name }}</span>
                  </li>
                  <li v-if="!(plan?.membership_perks || []).length" class="membership-pricing-feature text-muted">
                    <i class="bi bi-dash-circle-fill" aria-hidden="true"></i>
                    <span>No perks listed</span>
                  </li>
                </ul>

                <button
                  v-if="(plan?.membership_perks || []).length > 5"
                  type="button"
                  class="btn btn-link btn-sm p-0 membership-pricing-more"
                  @click.stop="toggle_perks(plan.id)"
                >
                  <span>{{ expandedPerks[plan.id] ? 'See less benefits' : 'See more benefits' }}</span>
                  <i
                    class="bi ms-1"
                    :class="expandedPerks[plan.id] ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </article>
          </div>
        </div>
        <div v-else class="py-5">
          <div class="d-flex flex-column align-items-center justify-content-center text-center text-muted py-4">
            <p class="mb-0">No memberships found</p>
          </div>
        </div>

      </div>


      <div v-if="currentStepIndex === 1" class="step-content">
        <div class="d-flex align-items-center gap-2 mb-3">
          <button
            class="btn rounded-circle p-2 d-flex align-items-center justify-content-center membership-view-back-btn membership-payment-back"
            @click="prevStep"
            aria-label="Back to memberships"
          >
            <i class="bi bi-arrow-left fs-4" aria-hidden="true"></i>
          </button>
          <h2 class="membership-payment-heading mb-0">My Subscription</h2>
        </div>

        <div v-if="selected_membership_plan" class="membership-payment-shell">
          <section class="membership-payment-plan-card manage-membership-single-card">
            <div class="fw-bold">{{ upgrade_quote ? 'Selected upgrade' : 'Current plan' }}</div>
            <div class="d-flex align-items-center gap-3 flex-wrap mb-4">
              <h4 class="membership-payment-plan-name mb-0">
                {{ selected_membership_plan.membership_type?.name }}
              </h4>
              <span
                class="badge ms-auto"
                :class="is_current_plan(selected_membership_plan) ? 'task-chip task-chip-status' : 'task-chip task-chip-tone-7'"
              >
                <i class="bi bi-circle-fill text-xxxs me-1" aria-hidden="true"></i>
                {{ is_current_plan(selected_membership_plan) ? 'active' : 'selected' }}
              </span>
            </div>

            <div class="membership-payment-summary-row">
              <div class="membership-payment-summary-item h-100 d-flex align-items-center w-100">
                <div class="d-flex align-items-center gap-3 min-w-0">
                  <span class="membership-payment-summary-icon">
                    <i class="bi bi-calendar-check" aria-hidden="true"></i>
                  </span>
                  <div class="min-w-0">
                    <div class="fw-bold text-xs">Billing</div>
                    <div class="fw-bold text-muted text-xs">
                      {{ selected_membership_plan.billing_cycle?.name || '-' }}
                    </div>
                  </div>
                </div>

                <div class="ms-auto text-end">
                  <div class="fw-bold text-xs">Price</div>
                  <div class="fw-bold text-muted text-xs">
                    {{ format_currency(selected_membership_plan.membership_pricing?.[0]?.price) }}
                  </div>
                </div>
              </div>
            </div>

          <div class="membership-payment-grid mt-4 manage-membership-inner-grid">
            <section class="membership-payment-detail-card">
              <div v-if="upgrade_quote">
                <h4 class="membership-payment-section-title">Upgrade cost comparison</h4>
                <div class="membership-upgrade-comparison">
                  <div class="d-flex justify-content-between gap-3 py-2 border-bottom">
                    <span class="text-muted">
                      Current: {{ upgrade_quote.current_membership?.name }}
                    </span>
                    <strong>{{ format_currency(upgrade_quote.current_membership?.daily_cost) }} / day</strong>
                  </div>
                  <div class="d-flex justify-content-between gap-3 py-2 border-bottom">
                    <span class="text-muted">
                      New: {{ upgrade_quote.new_membership?.name }}
                    </span>
                    <strong>{{ format_currency(upgrade_quote.new_membership?.daily_cost) }} / day</strong>
                  </div>
                  <div class="d-flex justify-content-between gap-3 py-2 border-bottom">
                    <span class="text-muted">Daily increase</span>
                    <strong>{{ format_currency(upgrade_quote.daily_cost_difference) }}</strong>
                  </div>
                  <div class="d-flex justify-content-between gap-3 py-2 border-bottom">
                    <span class="text-muted">Days remaining</span>
                    <strong>{{ upgrade_quote.remaining_days }}</strong>
                  </div>
                  <div class="d-flex justify-content-between gap-3 py-2">
                    <span class="fw-bold">Amount due now</span>
                    <strong class="text-primary">
                      {{ format_currency(upgrade_quote.prorated_upgrade_amount) }}
                    </strong>
                  </div>
                  <div class="d-flex justify-content-between gap-3 pt-2 mt-1 border-top">
                    <span class="text-muted">Next renewal</span>
                    <strong>{{ format_display_date(upgrade_quote.next_billing_date) }}</strong>
                  </div>
                </div>
              </div>
              <div v-else>
                <ul
                  v-if="selected_membership_plan.membership_perks && selected_membership_plan.membership_perks.length"
                  class="list-unstyled membership-payment-perks mb-0">
                  <li
                    v-for="perk in selected_membership_plan.membership_perks"
                    :key="perk.id">
                    <i class="bi bi-check-lg" aria-hidden="true"></i>
                    <span>{{ perk?.perk?.name }}</span>
                  </li>
                </ul>
                <p v-else class="text-muted small fst-italic mb-0">No perks listed for this membership.</p>
              </div>
            </section>

            <aside class="membership-payment-action-card">
              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="bi bi-info-circle-fill" aria-hidden="true"></i>
                <h4 class="membership-payment-section-title mb-0">Ready to continue?</h4>
              </div>
              <p class="text-muted text-xs mb-3">
                {{
                  upgrade_quote
                    ? 'Your current membership remains active until PayFast confirms the upgrade payment.'
                    : 'Review your plan, then subscribe to activate your membership.'
                }}
              </p>
              <button
                class="btn membership-payment-submit"
                @click="completePayment"
                :disabled="payment_submitting || upgrade_quote_loading">
                <span>
                  {{
                    payment_submitting
                      ? 'Processing...'
                      : upgrade_quote
                        ? `Upgrade and pay ${format_currency(upgrade_quote.prorated_upgrade_amount)}`
                        : 'Subscribe'
                  }}
                </span>
                <i class="bi bi-chevron-right" aria-hidden="true"></i>
              </button>
            </aside>
          </div>
          </section>
        </div>

</div>

    <div
      class="modal fade"
      id="membership_upgrade_confirmation_modal"
      tabindex="-1"
      aria-labelledby="membershipUpgradeConfirmationLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered membership-upgrade-dialog modal-fullscreen-sm-down">
        <div class="modal-content overflow-hidden border rounded shadow">
          <div class="modal-header align-items-start border-0 bg-white shadow-sm px-4 py-3">
            <div>
              <h5 class ="modal-title text-xs" id="membershipUpgradeConfirmationLabel">
                Confirm your upgrade
              </h5>
            </div>
            <button
              type="button"
              class="btn-close membership-upgrade-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body p-0" v-if="upgrade_quote">
            <div class="membership-upgrade-plan-comparison">
              <div class="d-flex flex-column border rounded bg-white p-3 text-muted membership-upgrade-plan-card">
                <div class="text-capitalize text-xxs fw-bold text-primary">{{ upgrade_quote.current_membership?.name }}</div>
                <span>{{ format_currency(upgrade_quote.current_membership?.price) }}</span>
              </div>
              <span class="d-flex align-items-center justify-content-center rounded-circle bg-white membership-upgrade-arrow" aria-hidden="true">
                <i class="bi bi-arrow-right"></i>
              </span>
              <div class="d-flex flex-column border rounded bg-white p-3 text-muted membership-upgrade-plan-card membership-upgrade-plan-card-new">
                <div class="text-capitalize text-xxs fw-bold text-primary">
                  {{ upgrade_quote.new_membership?.name || selected_membership_plan?.membership_type?.name }}
                </div>
                <span>{{ format_currency(upgrade_quote.new_membership?.price) }}</span>
              </div>
            </div>

            <div class="membership-upgrade-calculation">
              <div class="d-flex justify-content-between gap-3 py-2 text-muted text-xs membership-upgrade-calc-row">
                <span>
                  {{ upgrade_quote.new_membership?.name }} · {{ upgrade_quote.remaining_days }} days remaining
                </span>
                <strong>
                  {{ format_currency(get_prorated_plan_cost(upgrade_quote.new_membership?.daily_cost)) }}
                </strong>
              </div>
              <div class="d-flex justify-content-between gap-3 py-2 text-muted text-xs membership-upgrade-calc-row">
                <span>Unused {{ upgrade_quote.current_membership?.name }} credit</span>
                <strong class="membership-upgrade-credit">
                  − {{ format_currency(get_prorated_plan_cost(upgrade_quote.current_membership?.daily_cost)) }}
                </strong>
              </div>

              <div class="d-flex align-items-center justify-content-between gap-3 mt-2 pt-3 border-top membership-upgrade-due">
                <div class="d-flex flex-column">
                  <div class="text-dark fw-bold">Due today</div>
                  <span class="text-muted text-xs">Prorated difference</span>
                </div>
                <div class="text-dark fw-bold">
                  {{ format_currency(upgrade_quote.prorated_upgrade_amount) }}
                </div>
              </div>

              <div class="d-flex align-items-start gap-2 mt-3 p-3 rounded text-muted text-xs membership-upgrade-renewal">
                <i class="bi bi-info-circle" aria-hidden="true"></i>
                <span>
                  From {{ format_display_date(upgrade_quote.next_billing_date) }} you’ll be billed
                  {{ format_currency(upgrade_quote.new_membership?.price) }} per cycle.
                </span>
              </div>

              <ul v-if="get_upgrade_perks().length" class="list-unstyled d-grid gap-2 mt-3 mb-0 p-0 membership-upgrade-perks">
                <li v-for="perk in get_upgrade_perks()" :key="perk.id" class="d-flex align-items-center gap-2 text-dark text-xs">
                  <i class="bi bi-check-lg" aria-hidden="true"></i>
                  <span>{{ perk?.perk?.name }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="modal-footer d-flex justify-content-end border-top bg-white px-4 py-3">
            <button
              type="button"
              class="btn btn-sm bg-primary text-white rounded shadow"
              @click="completePayment"
              :disabled="payment_submitting || !upgrade_quote"
            >
              <span
                v-if="payment_submitting"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{
                payment_submitting
                  ? 'Redirecting...'
                  : 'Continue'
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="manage_subscription_modal"
      tabindex="-1"
      aria-labelledby="manageSubscriptionModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 border-0 shadow">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title text-primary fw-bold" id="manageSubscriptionModalLabel">Manage Subscription</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body pt-2" v-if="current_membership">
            <div class="mb-3">
              <div class="small text-muted mb-1">Plan</div>
              <div class="fw-bold text-dark">{{ current_membership.membership_type?.name || current_membership.name }}</div>
            </div>
            <div class="mb-3">
              <div class="small text-muted mb-1">Status</div>
              <div class="fw-semibold" :class="current_membership.is_active ? 'text-success' : 'text-danger'">
                {{ current_membership.is_active ? 'Active' : 'Inactive' }}
              </div>
            </div>
            <div class="mb-3">
              <div class="small text-muted mb-1">Billing cycle</div>
              <div class="fw-medium text-dark">{{ current_membership.billing_cycle?.name || '-' }}</div>
            </div>
            <div>
              <div class="small text-muted mb-1">Price</div>
              <div class="fw-medium text-dark">
                {{ format_currency(current_membership.membership_pricing?.[0]?.price) }}
              </div>
            </div>
            <button type="button" class="btn btn-primary btn-sm mt-4" @click="get_membershipcard">
              <i class="bi bi-person-vcard me-2"></i>View membership card
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>